// Pricing configuration for diagnostics
export const diagnosticPricing = {
  // DPE avec tarifs officiels selon type, surface/pièces et année
  dpe: {
    name: "DPE - Diagnostic de Performance Énergétique",
    category: "particulier",
    requiresYear: true,
    appartement: {
      // Surface approximative: Studio=25m², T2=45m², T3=65m², T4=85m², T5=105m²
      ranges: [
        { maxSurface: 35, label: "Studio/F1", 
          prices: { "2007+": 160, "1997-2006": 170, "1949-1997": 190, "-1949": 220 } },
        { maxSurface: 55, label: "T2", 
          prices: { "2007+": 190, "1997-2006": 200, "1949-1997": 220, "-1949": 240 } },
        { maxSurface: 75, label: "T3", 
          prices: { "2007+": 200, "1997-2006": 230, "1949-1997": 240, "-1949": 290 } },
        { maxSurface: 95, label: "T4", 
          prices: { "2007+": 210, "1997-2006": 250, "1949-1997": 270, "-1949": 310 } },
        { maxSurface: Infinity, label: "T5+", 
          prices: { "2007+": 220, "1997-2006": 260, "1949-1997": 280, "-1949": 340 } }
      ]
    },
    maison: {
      ranges: [
        { maxSurface: 50, label: "< 50m²", 
          prices: { "2007+": 190, "1997-2006": 240, "1949-1997": 290, "-1949": 320 } },
        { maxSurface: 100, label: "50-100m²", 
          prices: { "2007+": 220, "1997-2006": 280, "1949-1997": 340, "-1949": 370 } },
        { maxSurface: 150, label: "100-150m²", 
          prices: { "2007+": 240, "1997-2006": 310, "1949-1997": 360, "-1949": 390 } },
        { maxSurface: 200, label: "150-200m²", 
          prices: { "2007+": 270, "1997-2006": 340, "1949-1997": 390, "-1949": 460 } },
        { maxSurface: Infinity, label: "> 200m²", 
          prices: { "2007+": 300, "1997-2006": 370, "1949-1997": 420, "-1949": 490 } } // Extrapolé
      ]
    }
  },
  
  amiante: {
    name: "Diagnostic Amiante",
    category: "particulier",
    prices: [
      { maxSurface: 50, price: 90 },
      { maxSurface: 100, price: 120 },
      { maxSurface: Infinity, price: 150 }
    ]
  },
  plomb: {
    name: "Diagnostic Plomb (CREP)",
    category: "particulier",
    prices: [
      { maxSurface: 50, price: 90 },
      { maxSurface: 100, price: 110 },
      { maxSurface: Infinity, price: 140 }
    ]
  },
  termites: {
    name: "Diagnostic Termites",
    category: "particulier",
    prices: [
      { maxSurface: 50, price: 120 },
      { maxSurface: 100, price: 150 },
      { maxSurface: Infinity, price: 180 }
    ]
  },
  gaz: {
    name: "Diagnostic Gaz",
    category: "particulier",
    prices: [
      { maxSurface: Infinity, price: 80 }
    ]
  },
  electricite: {
    name: "Diagnostic Électricité",
    category: "particulier",
    prices: [
      { maxSurface: Infinity, price: 90 }
    ]
  },
  carrez: {
    name: "Loi Carrez / Boutin",
    category: "particulier",
    prices: [
      { maxSurface: 50, price: 70 },
      { maxSurface: 100, price: 90 },
      { maxSurface: Infinity, price: 110 }
    ]
  },
  erp: {
    name: "ERP - État des Risques et Pollutions",
    category: "particulier",
    prices: [
      { maxSurface: Infinity, price: 30 }
    ]
  },
  
  // Diagnostics professionnels / copropriétés
  dtg: {
    name: "DTG - Diagnostic Technique Global",
    category: "professionnel",
    prices: [
      { maxSurface: 1000, price: 800 },
      { maxSurface: 2000, price: 1200 },
      { maxSurface: Infinity, price: 1500 }
    ]
  },
  dpe_collectif: {
    name: "DPE Collectif",
    category: "professionnel",
    prices: [
      { maxSurface: 500, price: 600 },
      { maxSurface: 1000, price: 900 },
      { maxSurface: Infinity, price: 1200 }
    ]
  },
  ppt: {
    name: "PPT - Plan Pluriannuel de Travaux",
    category: "professionnel",
    prices: [
      { maxSurface: Infinity, price: 1000 }
    ]
  }
};

// Get year period for DPE pricing
const getYearPeriod = (year) => {
  if (!year) return "2007+"; // Default to most recent
  if (year >= 2007) return "2007+";
  if (year >= 1997) return "1997-2006";
  if (year >= 1949) return "1949-1997";
  return "-1949";
};

// Calculate price for a single diagnostic
export const calculateDiagnosticPrice = (diagnosticKey, surface, propertyType = 'appartement', constructionYear = null) => {
  const diagnostic = diagnosticPricing[diagnosticKey];
  if (!diagnostic) return 0;
  
  // Special handling for DPE
  if (diagnosticKey === 'dpe') {
    const yearPeriod = getYearPeriod(constructionYear);
    const typeData = propertyType === 'maison' ? diagnostic.maison : diagnostic.appartement;
    
    for (const range of typeData.ranges) {
      if (surface <= range.maxSurface) {
        return range.prices[yearPeriod];
      }
    }
    
    // Fallback to last range
    const lastRange = typeData.ranges[typeData.ranges.length - 1];
    return lastRange.prices[yearPeriod];
  }
  
  // Standard pricing for other diagnostics
  for (const tier of diagnostic.prices) {
    if (surface <= tier.maxSurface) {
      return tier.price;
    }
  }
  
  return diagnostic.prices[diagnostic.prices.length - 1].price;
};

// Calculate total with discount
export const calculateTotal = (selectedDiagnostics, surface, propertyType = 'appartement', constructionYear = null) => {
  const count = selectedDiagnostics.length;
  
  let subtotal = 0;
  const details = selectedDiagnostics.map(key => {
    const price = calculateDiagnosticPrice(key, surface, propertyType, constructionYear);
    subtotal += price;
    return {
      key,
      name: diagnosticPricing[key].name,
      price
    };
  });
  
  // Apply discounts
  let discount = 0;
  let discountRate = 0;
  
  if (count >= 4) {
    discountRate = 15;
    discount = Math.round(subtotal * 0.15);
  } else if (count >= 2) {
    discountRate = 10;
    discount = Math.round(subtotal * 0.10);
  }
  
  const total = subtotal - discount;
  
  return {
    details,
    subtotal,
    discount,
    discountRate,
    total,
    count
  };
};
